'use client';

import Grid from '@mui/material/Grid';
import { gql, useLazyQuery, useMutation } from '@parkchirp/api-client';
import { cloneDeep } from 'lodash';
import { BillingProfileBillingMonthEnum } from 'parkchirp-shared/enums';
import { useCallback, useEffect, useState } from 'react';

import { SetShowSnackbar } from '../../../types';
import { Dayjs, dayjs } from '../../../utils';
import { RateHistoryTable } from './rateHistoryTable';
import { SublotHistoryTable } from './sublotHistoryTable';

type HistoryTablesProps = {
	parkerId: string;
	billingProfileId: string;
	terminationStatus: string;
	billingStartDate: Dayjs;
	sublots: { id: number; name: string }[];
	parkerLoaded: boolean;
	setShowSnackbar: SetShowSnackbar;
	tenantSubscriptionId?: string;
	isEditMode: boolean;
	refetchParkersFromCache?: () => void;
	usesDefaultRates: boolean;
	facilityId: string;
};

const GET_BILLING_PROFILE = gql`
    query getBillingProfile(
        $billingProfileId: String!,
        $tenantSubscriptionId: String
    ) {
        getBillingProfile(
            billingProfileId: $billingProfileId,
            tenantSubscriptionId: $tenantSubscriptionId
        ) {
            billingDay
            billingMonth
        }
    }
`;

const GET_PARKER_SUBLOT_HISTORY = gql`
    query getParkerSublotHistory(
        $parkerId: String!,
        $tenantSubscriptionId: String
    ) {
        getParkerSublotHistory(
            parkerId: $parkerId,
            tenantSubscriptionId: $tenantSubscriptionId
        ) {
            id,
            startDate,
            sublot {
                id
                name
            }
        }
    }
`;

const DELETE_PARKER_SUBLOT_HISTORY = gql`
    mutation deleteParkerSublotHistory(
        $parkerSublotHistoryId: Int!,
        $tenantSubscriptionId: String
    ) {
        deleteParkerSublotHistory(
            parkerSublotHistoryId: $parkerSublotHistoryId,
            tenantSubscriptionId: $tenantSubscriptionId
        )
    }
`;

const UPDATE_PARKER_SUBLOT_HISTORY = gql`
    mutation updateParkerSublotHistory(
        $parkerSublotHistoryId: Int!,
        $sublotId: Int!,
        $startDate: String!,
        $tenantSubscriptionId: String
    ) {
        updateParkerSublotHistory(
            parkerSublotHistoryId: $parkerSublotHistoryId,
            sublotId: $sublotId,
            startDate: $startDate,
            tenantSubscriptionId: $tenantSubscriptionId
        )
    }
`;

const { CURRENT_MONTH } = BillingProfileBillingMonthEnum;

export const RateAndSublotHistoryTables = ({
	parkerId,
	billingProfileId,
	terminationStatus,
	billingStartDate,
	sublots,
	parkerLoaded,
	setShowSnackbar,
	tenantSubscriptionId,
	isEditMode,
	refetchParkersFromCache,
	usesDefaultRates,
	facilityId,
}: HistoryTablesProps) => {
	const [billingDay, setBillingDay] = useState<number>(null);
	const [billingMonth, setBillingMonth] = useState<BillingProfileBillingMonthEnum>(null);
	const [sublotHistory, setSublotHistory] = useState([]);
	const [getParkerSublotHistory, { loading: sublotHistoryLoading }] = useLazyQuery(GET_PARKER_SUBLOT_HISTORY);
	const [deleteParkerSublotHistory, { loading: deleteInProgress }] = useMutation(DELETE_PARKER_SUBLOT_HISTORY);
	const [updateParkerSublotHistory, { loading: updateInProgress }] = useMutation(UPDATE_PARKER_SUBLOT_HISTORY);

	const [getBillingProfile] = useLazyQuery(GET_BILLING_PROFILE, {
		onError: () => {
			setShowSnackbar({
				message: 'Failed to fetch billing profile data',
				autoHideDuration: 5000,
				severity: 'error',
			});
		},
	});

	const fetchBillingProfile = useCallback(async () => {
		const { data: billingProfileData } = await getBillingProfile({
			variables: { billingProfileId, tenantSubscriptionId },
			context: {
				apiVersion: 'v2',
				appName: process.env.NEXT_PUBLIC_APP_NAME,
			},
			fetchPolicy: 'network-only',
		});

		setBillingDay(billingProfileData.getBillingProfile.billingDay);
		setBillingMonth(billingProfileData.getBillingProfile.billingMonth);
	}, [billingProfileId, getBillingProfile, tenantSubscriptionId]);

	useEffect(() => {
		if (billingProfileId && !billingDay) {
			fetchBillingProfile();
		}
	}, [billingDay, billingProfileId, fetchBillingProfile]);

	const parkerHasBeenInvoiced = useCallback(
		(historyStartDate: string) => {
			if (['PENDING', 'NOT_INVOICED'].includes(terminationStatus)) return false;
			const date = dayjs.utc(historyStartDate);
			const now = dayjs.utc();

			const invoicedFuture = date.isAfter(now) && date.isSame(billingStartDate, 'month');
			const invoicedCurrentMonth = date.isSameOrBefore(now, 'month');

			if (billingMonth === CURRENT_MONTH) {
				return invoicedCurrentMonth || invoicedFuture;
			}

			const nextMonth = now.add(1, 'month');

			const invoicedNextMonth = date.isSame(nextMonth, 'month') && Number(now.format('D')) >= billingDay;

			if (invoicedCurrentMonth || invoicedNextMonth || invoicedFuture) return true;

			return false;
		},
		[billingDay, billingMonth, billingStartDate, terminationStatus],
	);

	const fetchParkerSublotHistory = useCallback(async () => {
		const { data: parkerSublotHistoryData } = await getParkerSublotHistory({
			variables: { parkerId, tenantSubscriptionId },
			context: {
				apiVersion: 'v2',
				appName: process.env.NEXT_PUBLIC_APP_NAME,
			},
			fetchPolicy: 'network-only',
		});

		const parkerSublotHistory = parkerSublotHistoryData.getParkerSublotHistory.map((sublotHistoryRecord) => ({
			id: sublotHistoryRecord.id,
			startDate: sublotHistoryRecord.startDate,
			sublotId: sublotHistoryRecord.sublot.id,
			sublotName: sublotHistoryRecord.sublot.name,
		}));

		setSublotHistory(cloneDeep(parkerSublotHistory));
	}, [getParkerSublotHistory, parkerId, tenantSubscriptionId]);

	useEffect(() => {
		fetchParkerSublotHistory();
	}, [fetchParkerSublotHistory]);

	return (
		<>
			<Grid item xs={6}>
				<RateHistoryTable
					parkerId={parkerId}
					terminationStatus={terminationStatus}
					billingDay={billingDay}
					parkerLoaded={parkerLoaded}
					parkerHasBeenInvoiced={parkerHasBeenInvoiced}
					setShowSnackbar={setShowSnackbar}
					tenantSubscriptionId={tenantSubscriptionId}
					isEditMode={isEditMode}
					refetchParkersFromCache={refetchParkersFromCache}
					usesDefaultRates={usesDefaultRates}
					fetchParkerSublotHistory={fetchParkerSublotHistory}
					deleteParkerSublotHistory={deleteParkerSublotHistory}
					sublotHistory={sublotHistory}
					updateParkerSublotHistory={updateParkerSublotHistory}
					facilityId={facilityId}
				/>
			</Grid>
			<Grid item xs={6}>
				<SublotHistoryTable
					parkerId={parkerId}
					terminationStatus={terminationStatus}
					billingDay={billingDay}
					parkerLoaded={parkerLoaded}
					sublots={sublots}
					parkerHasBeenInvoiced={parkerHasBeenInvoiced}
					setShowSnackbar={setShowSnackbar}
					tenantSubscriptionId={tenantSubscriptionId}
					isEditMode={isEditMode}
					refetchParkersFromCache={refetchParkersFromCache}
					sublotHistory={sublotHistory}
					sublotHistoryLoading={sublotHistoryLoading}
					fetchParkerSublotHistory={fetchParkerSublotHistory}
					deleteParkerSublotHistory={deleteParkerSublotHistory}
					deleteInProgress={deleteInProgress}
					updateParkerSublotHistory={updateParkerSublotHistory}
					updateInProgress={updateInProgress}
				/>
			</Grid>
		</>
	);
};
